import { Button } from "@/components/ui/button";
import { Link } from "wouter";
import { Users, Anchor, Calendar, Trophy } from "lucide-react";
// Using direct path since we serve /attached_assets statically

export default function Home() {
  return (
    <div>
      {/* Hero Section */}
      <section className="relative min-h-[85vh] bg-gradient-navy overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat" 
          style={{ backgroundImage: `url(/attached_assets/hero_1750152204984.jpg)` }}
        />
        <div className="absolute inset-0 bg-black bg-opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-transparent to-black/40" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center min-h-[85vh]">
          <div className="text-white max-w-3xl">
            <div className="mb-6">
              <span className="inline-block px-4 py-2 rounded-full bg-white/20 text-white font-medium text-sm mb-4 backdrop-blur-sm border border-white/30">
                Established 1968 • Vaal Dam
              </span>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight text-white drop-shadow-lg">
              Welcome to
              <span className="block text-orange-300">VCA</span>
            </h1>
            <p className="text-xl md:text-2xl mb-8 max-w-2xl text-white leading-relaxed drop-shadow-md">
              Discover the joy of boating and cruising with South Africa's premier boating association on the beautiful Vaal Dam.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link href="/contact">
                <Button className="bg-orange-300 hover:bg-orange-400 text-black font-semibold py-4 px-8 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all duration-200">
                  Contact Us
                </Button>
              </Link>
              <Link href="/history">
                <Button className="bg-orange-300 hover:bg-orange-400 text-black font-semibold py-4 px-8 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all duration-200">
                  Learn More
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Links Section */}
      <section className="py-24 bg-soft-gray">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-navy mb-6">Explore VCA</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Everything you need to know about our boating community
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Link href="/contact">
              <div className="card-modern p-8 text-center cursor-pointer group">
                <div className="w-16 h-16 bg-sailing-gold rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-200">
                  <Users className="text-navy h-8 w-8" />
                </div>
                <h3 className="text-xl font-bold text-navy mb-3">Contact Us</h3>
                <p className="text-gray-600 leading-relaxed">Get in touch to learn about membership and activities</p>
              </div>
            </Link>

            <Link href="/rules">
              <div className="card-modern p-8 text-center cursor-pointer group">
                <div className="w-16 h-16 bg-sailing-gold rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-200">
                  <Anchor className="text-navy h-8 w-8" />
                </div>
                <h3 className="text-xl font-bold text-navy mb-3">Club Rules</h3>
                <p className="text-gray-600 leading-relaxed">Safety guidelines and regulations for all members</p>
              </div>
            </Link>

            <Link href="/weather">
              <div className="card-modern p-8 text-center cursor-pointer group">
                <div className="w-16 h-16 bg-sailing-gold rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-200">
                  <Calendar className="text-navy h-8 w-8" />
                </div>
                <h3 className="text-xl font-bold text-navy mb-3">Weather</h3>
                <p className="text-gray-600 leading-relaxed">Current conditions and forecasts for Vaal Dam</p>
              </div>
            </Link>

            <Link href="/history">
              <div className="card-modern p-8 text-center cursor-pointer group">
                <div className="w-16 h-16 bg-sailing-gold rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-200">
                  <Trophy className="text-navy h-8 w-8" />
                </div>
                <h3 className="text-xl font-bold text-navy mb-3">Our History</h3>
                <p className="text-gray-600 leading-relaxed">57 years of boating excellence and community</p>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* About Preview Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold text-navy mb-6">About VCA</h2>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                The Vaal Cruising Association has been South Africa's premier boating club since 1968. Located on the beautiful Vaal Dam, we offer a welcoming community for boating enthusiasts of all levels.
              </p>
              <div className="space-y-4 mb-8">
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-sailing-gold rounded-full mr-4"></div>
                  <span className="text-gray-700">57+ years of boating excellence</span>
                </div>
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-sailing-gold rounded-full mr-4"></div>
                  <span className="text-gray-700">Premier location on Vaal Dam</span>
                </div>
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-sailing-gold rounded-full mr-4"></div>
                  <span className="text-gray-700">Welcoming community for all skill levels</span>
                </div>
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-sailing-gold rounded-full mr-4"></div>
                  <span className="text-gray-700">Full facilities and amenities</span>
                </div>
              </div>
              <Link href="/about">
                <Button className="bg-navy hover:bg-blue-800 text-white font-semibold py-3 px-6">
                  Learn More About Us
                </Button>
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <img 
                src="/attached_assets/VCA photos (359) - Copy_1750074778100.jpg" 
                alt="VCA Boats" 
                className="w-full h-48 object-cover rounded-lg shadow-lg"
              />
              <img 
                src="/attached_assets/VCA photos (587) - Copy_1750074778094.jpg" 
                alt="VCA Facilities" 
                className="w-full h-48 object-cover rounded-lg shadow-lg mt-8"
              />
              <img 
                src="/attached_assets/VCA photos (554) - Copy_1750074778100.JPG" 
                alt="VCA Dam View" 
                className="w-full h-48 object-cover rounded-lg shadow-lg"
              />
              <img 
                src="/attached_assets/VCA photos (562)_1750074778101.jpg" 
                alt="VCA Activities" 
                className="w-full h-48 object-cover rounded-lg shadow-lg mt-8"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Contact Preview Section */}
      <section className="py-24 bg-gradient-navy text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Join VCA?</h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto leading-relaxed text-gray-100">
            Contact us today to learn more about membership or visit our facilities on the beautiful Vaal Dam.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link href="/contact">
              <Button className="bg-orange-300 hover:bg-orange-400 text-navy font-semibold py-3 px-8">
                Get in Touch
              </Button>
            </Link>
          </div>
          <div className="mt-12 text-center">
            <p className="text-gray-300 mb-2">
              <strong>Email:</strong> info@vaalcruising.co.za | <strong>Phone:</strong> 016 004 0192
            </p>
            <p className="text-gray-300">
              Aloe Fjord, Vaal Dam, Gauteng, South Africa
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}